/**
 * v3.21-1762 · Lender Offers Workspace
 *
 * Replaces the flat 88-row Lender Offers table with:
 *   1. RECOMMENDED OFFERS  — the deterministic Top-2 (payment + term + APR
 *                            rank, evaluated only within the meaningful
 *                            qualifying pool; a $1,000 approval with a $27
 *                            payment can NEVER outrank a genuine $20,000
 *                            offer for a $16,000 request).
 *   2. ALL OFFERS          — grouped by APPROVED AMOUNT (high → low),
 *                            collapsible accordion, collapsed by default.
 *
 * Selection SSOT
 * --------------
 * Every clickable Select control — inside a Recommendation card OR inside
 * an Approved-Amount group row — dispatches `onToggleSelection(originalIndex)`
 * against the SAME `selectedIndices: number[]` prop. The SAME underlying
 * offer appearing in Recommendations AND in a group is ONE selection, not
 * two. `handleSetOffers` in the parent consumes `selectedIndices` unchanged
 * — no fabricated offer objects are ever created here.
 *
 * External-partner privacy
 * ------------------------
 * The server (`services/lender_identity_scrub.py`, preserved from
 * v3.21-1758) has already redacted lender identity in the offers[] payload
 * for external partners. This component renders whatever the server
 * returned — no client-side reconstruction of lender identity.
 */
import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Sparkles, CheckCircle2 } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  pickTopTwoRecommendations,
  groupOffersByApprovedAmount,
} from '../lib/offerRanking';

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtMoneyDecimal = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtApr = (n) => `${Number(n || 0).toFixed(2)}%`;
const fmtTerm = (n) => `${n} months`;

const lenderLabel = (offer, isInternal, fallbackIndex) => {
  if (!isInternal) return `Option ${fallbackIndex + 1}`;
  return offer?.lender_name || offer?.lender || offer?.name || 'Unknown';
};

const recommendationReasonLabel = (mode) => {
  if (mode === 'fee_protected_ideal')     return 'Best Fit for Request';
  if (mode === 'fee_protected_nearest')   return 'Closest Fee-Protected Approval';
  if (mode === 'covers_request')          return 'Best Overall Terms';
  if (mode === 'highest_tier_fallback')   return 'Highest Available Approval';
  if (mode === 'no_meaningful_offers')    return '';
  return '';
};

function RecommendationCard({ rank, item, isInternal, selectedIndices, onToggleSelection, reasonLabel }) {
  const { raw, originalIndex, approved, term, apr, payment } = item;
  const selIdx = selectedIndices.indexOf(originalIndex);
  const isSelected = selIdx >= 0;
  const selectLabel = selIdx === 0 ? 'Offer 1' : selIdx === 1 ? 'Offer 2' : 'Select';
  return (
    <div
      className={`rounded-lg border-2 p-4 space-y-3 transition-colors ${
        isSelected
          ? (selIdx === 0 ? 'border-blue-500 bg-blue-50/70' : 'border-emerald-500 bg-emerald-50/70')
          : 'border-amber-300 bg-gradient-to-br from-amber-50 to-white'
      }`}
      data-testid={`recommended-offer-card-${rank}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-amber-600" />
          <span className="text-[11px] font-bold uppercase tracking-wide text-amber-800">
            Recommended #{rank}
          </span>
        </div>
        {reasonLabel && (
          <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-300" data-testid={`recommended-reason-${rank}`}>
            {reasonLabel}
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <div>
          <div className="text-[10px] text-slate-500 uppercase">Approved</div>
          <div className="text-base font-bold text-emerald-700" data-testid={`recommended-approved-${rank}`}>
            {fmtMoney(approved)}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 uppercase">Term</div>
          <div className="font-semibold text-slate-800" data-testid={`recommended-term-${rank}`}>
            {term > 0 ? fmtTerm(term) : '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 uppercase">APR</div>
          <div className="font-semibold text-slate-800" data-testid={`recommended-apr-${rank}`}>
            {apr > 0 ? fmtApr(apr) : '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 uppercase">Monthly Payment</div>
          <div className="font-semibold text-slate-800" data-testid={`recommended-payment-${rank}`}>
            {payment > 0 ? `${fmtMoneyDecimal(payment)}/mo` : '—'}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-1 border-t border-amber-100">
        <div className="text-[11px] text-slate-600" data-testid={`recommended-lender-${rank}`}>
          {lenderLabel(raw, isInternal, originalIndex)}
        </div>
        <button
          type="button"
          onClick={() => onToggleSelection(originalIndex)}
          className={`px-3 py-1 text-[11px] font-semibold rounded border transition-colors ${
            selIdx === 0
              ? 'bg-blue-600 text-white border-blue-600'
              : selIdx === 1
              ? 'bg-emerald-600 text-white border-emerald-600'
              : 'bg-white text-slate-700 border-amber-400 hover:bg-amber-50'
          }`}
          data-testid={`recommended-select-${rank}`}
        >
          {selectLabel}
        </button>
      </div>
    </div>
  );
}

function AmountGroup({ group, isInternal, selectedIndices, onToggleSelection, finturfSelectedOfferId, forceOpen }) {
  const [localOpen, setLocalOpen] = useState(false); // default: collapsed
  const open = forceOpen || localOpen;
  const { approvedAmount, count, termRange, aprRange, paymentRange, offers } = group;

  const rangeChip = (label, range, formatter, testid) => {
    if (!range) return null;
    const [lo, hi] = range;
    const val = lo === hi ? formatter(lo) : `${formatter(lo)}–${formatter(hi)}`;
    return (
      <span className="text-[10px] text-slate-500" data-testid={testid}>
        {label}: <span className="text-slate-700 font-medium">{val}</span>
      </span>
    );
  };

  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`amount-group-${approvedAmount}`}>
      <button
        type="button"
        onClick={() => setLocalOpen(v => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
        data-testid={`amount-group-toggle-${approvedAmount}`}
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? <ChevronDown className="h-4 w-4 text-slate-500 shrink-0" />
                : <ChevronRight className="h-4 w-4 text-slate-500 shrink-0" />}
          <span className="text-sm font-bold text-emerald-700 whitespace-nowrap" data-testid={`amount-group-amount-${approvedAmount}`}>
            {fmtMoney(approvedAmount)}
          </span>
          <span className="text-[11px] text-slate-500 whitespace-nowrap" data-testid={`amount-group-count-${approvedAmount}`}>
            {count} {count === 1 ? 'offer' : 'offers'}
          </span>
        </div>
        <div className="hidden sm:flex items-center gap-3 flex-wrap justify-end">
          {rangeChip('Term',    termRange,    (v) => `${v} mo`,  `amount-group-term-range-${approvedAmount}`)}
          {rangeChip('APR',     aprRange,     fmtApr,            `amount-group-apr-range-${approvedAmount}`)}
          {rangeChip('Payment', paymentRange, (v) => `${fmtMoneyDecimal(v)}/mo`, `amount-group-payment-range-${approvedAmount}`)}
        </div>
      </button>

      {open && (
        <table className="w-full text-sm" data-testid={`amount-group-table-${approvedAmount}`}>
          <thead>
            <tr className="bg-white border-b text-[11px] text-slate-500 uppercase">
              <th className="px-3 py-2 text-left w-24">Shortlist</th>
              <th className="px-3 py-2 text-left">Lender</th>
              <th className="px-3 py-2 text-right">Term</th>
              <th className="px-3 py-2 text-right">APR</th>
              <th className="px-3 py-2 text-right">Monthly Payment</th>
              <th className="px-3 py-2 text-center w-28">PTC Finance</th>
            </tr>
          </thead>
          <tbody>
            {offers.map((item) => {
              const { raw, originalIndex, term, apr, payment, offerId } = item;
              const selIdx = selectedIndices.indexOf(originalIndex);
              const isSelected = selIdx >= 0;
              const selectLabel = selIdx === 0 ? 'Offer 1' : selIdx === 1 ? 'Offer 2' : 'Select';
              const finturfMatch = !!finturfSelectedOfferId && offerId && offerId === finturfSelectedOfferId;
              return (
                <tr
                  key={`amount-${approvedAmount}-offer-${originalIndex}`}
                  className={`border-b transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                  data-testid={`amount-group-row-${approvedAmount}-${originalIndex}`}
                >
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => onToggleSelection(originalIndex)}
                      className={`px-2 py-1 text-[11px] font-semibold rounded border transition-colors ${
                        selIdx === 0
                          ? 'bg-blue-600 text-white border-blue-600'
                          : selIdx === 1
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400'
                      }`}
                      data-testid={`amount-group-select-${approvedAmount}-${originalIndex}`}
                    >
                      {selectLabel}
                    </button>
                  </td>
                  <td className="px-3 py-2 font-medium text-slate-700" data-testid={`amount-group-lender-${approvedAmount}-${originalIndex}`}>
                    {lenderLabel(raw, isInternal, originalIndex)}
                  </td>
                  <td className="px-3 py-2 text-right" data-testid={`amount-group-term-${approvedAmount}-${originalIndex}`}>
                    {term > 0 ? `${term} mo` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right" data-testid={`amount-group-apr-${approvedAmount}-${originalIndex}`}>
                    {apr > 0 ? fmtApr(apr) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right" data-testid={`amount-group-payment-${approvedAmount}-${originalIndex}`}>
                    {payment > 0 ? `${fmtMoneyDecimal(payment)}/mo` : '—'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {finturfMatch ? (
                      <Badge className="text-[10px] bg-purple-100 text-purple-700 border-purple-300" data-testid={`amount-group-ptc-finance-selected-${approvedAmount}-${originalIndex}`}>
                        Selected
                      </Badge>
                    ) : (
                      <span className="text-[10px] text-slate-300">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function LenderOffersWorkspace({
  offers,
  requestedAmount,
  selectedIndices,
  onToggleSelection,
  isInternal,
  finturfSelectedOfferId,
}) {
  const { top: recommendations, mode: recommendationMode } = useMemo(
    () => pickTopTwoRecommendations(offers, requestedAmount),
    [offers, requestedAmount]
  );
  const groups = useMemo(
    () => groupOffersByApprovedAmount(offers),
    [offers]
  );
  const reasonLabel = recommendationReasonLabel(recommendationMode);

  const [expandAll, setExpandAll] = useState(false);
  const showAllOffersHeader = groups.length > 0;

  return (
    <div className="space-y-5" data-testid="lender-offers-workspace">
      {/* Explanatory helper text (branding is PTC Finance user-facing) */}
      <p className="text-xs text-slate-500" data-testid="lender-offers-workspace-intro">
        Every lender offer returned by PTC Finance for the current application is
        organized below. Shortlist up to 2 to copy into Offer 1 / Offer 2 —
        the actual consumer selection is still completed inside PTC Finance.
      </p>

      {/* RECOMMENDED OFFERS */}
      {recommendations.length === 0 && recommendationMode === 'no_fee_protected_offers' && (
        <div className="text-xs rounded-md border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2" data-testid="recommended-none-fee-protected">
          No offer is large enough to cover the Applied For amount plus a possible lender fee. Operator review required —
          all offers are still listed below.
        </div>
      )}
      {recommendations.length > 0 && (
        <section data-testid="recommended-offers-section" className="space-y-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-600" />
            <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">
              Recommended Offers
            </h3>
            {recommendationMode === 'highest_tier_fallback' && requestedAmount > 0 && (
              <Badge className="text-[10px] bg-slate-100 text-slate-600 border-slate-300" data-testid="recommended-fallback-badge">
                No offer fully covers the request — showing highest approval available
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {recommendations.map((item, i) => (
              <RecommendationCard
                key={`recommended-${item.originalIndex}`}
                rank={i + 1}
                item={item}
                isInternal={isInternal}
                selectedIndices={selectedIndices}
                onToggleSelection={onToggleSelection}
                reasonLabel={reasonLabel}
              />
            ))}
          </div>
        </section>
      )}

      {/* ALL OFFERS — grouped by approved amount */}
      {showAllOffersHeader && (
        <section data-testid="all-offers-section" className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700">
              All Offers
              <span className="ml-2 text-[11px] font-normal text-slate-500 normal-case tracking-normal">
                grouped by approved amount · highest first
              </span>
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExpandAll(v => !v)}
              data-testid="all-offers-expand-all"
              className="text-[11px] h-7"
            >
              {expandAll ? 'Collapse all' : 'Expand all'}
            </Button>
          </div>
          <div className="space-y-2">
            {groups.map((group) => (
              <AmountGroup
                key={`group-${group.approvedAmount}`}
                group={group}
                isInternal={isInternal}
                selectedIndices={selectedIndices}
                onToggleSelection={onToggleSelection}
                finturfSelectedOfferId={finturfSelectedOfferId}
                forceOpen={expandAll}
              />
            ))}
          </div>
          {expandAll && (
            <p className="text-[10px] text-slate-400 mt-1" data-testid="all-offers-expand-note">
              Click a group header to collapse it individually.
            </p>
          )}
        </section>
      )}

      {/* Selected-indicator row when a PTC Finance-selected offer was
          detected upstream. The specific matching row is highlighted
          inline inside its group above. */}
      {finturfSelectedOfferId && (
        <div className="flex items-center gap-2 text-[11px] text-purple-700" data-testid="workspace-ptc-finance-selected-note">
          <CheckCircle2 className="h-3.5 w-3.5" />
          A PTC Finance-selected offer is highlighted inside its Approved-Amount group.
        </div>
      )}
    </div>
  );
}
